import React, { useEffect, useState } from "react";
import { Mail, Search, Trophy, X } from "lucide-react";
import RankingPositionBadge from "./RankingPositionBadge";
import {
  getRankingCorrectAnswers,
  getRankingScore,
  getRankingStudentEmail,
  getRankingStudentName,
  getRankingTime,
} from "./teacherMocksHelpers";

export default function TeacherRankingDrawer({ open, mock, ranking, loading, error, onClose }) {
  const [search, setSearch] = useState("");
  const [scoreFilter, setScoreFilter] = useState("all");

  useEffect(() => {
    if (!open) return;

    setSearch("");
    setScoreFilter("all");

    function handleKeyDown(event) {
      if (event.key === "Escape") onClose();
    }

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [open, onClose]);

  if (!open) return null;

  const totalQuestions = Number(mock?.questions) || 0;
  const normalizedRanking = ranking.map((item, index) => ({
    ...item,
    position: Number(item.position) || index + 1,
    name: getRankingStudentName(item),
    email: getRankingStudentEmail(item),
    score: getRankingScore(item),
    correctAnswers: getRankingCorrectAnswers(item, totalQuestions),
    time: getRankingTime(item),
  }));

  const averageScore = normalizedRanking.length
    ? Math.round(normalizedRanking.reduce((sum, item) => sum + item.score, 0) / normalizedRanking.length)
    : 0;

  const filteredRanking = normalizedRanking.filter((item) => {
    const matchesSearch = `${item.name} ${item.email}`.toLowerCase().includes(search.trim().toLowerCase());
    if (!matchesSearch) return false;
    if (scoreFilter === "high") return item.score >= 70;
    if (scoreFilter === "medium") return item.score >= 50 && item.score < 70;
    if (scoreFilter === "low") return item.score < 50;
    return true;
  });

  const podium = [2, 1, 3]
    .map((position) => normalizedRanking.find((item) => item.position === position))
    .filter(Boolean);

  return (
    <div className="teacher-ranking-overlay" onMouseDown={onClose}>
      <aside
        className="teacher-ranking-drawer"
        role="dialog"
        aria-modal="true"
        aria-label="Ranking"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <header className="teacher-ranking-header">
          <div>
            <h2>
              <Trophy size={20} aria-hidden="true" />
              Ranking
            </h2>
            <p>{mock?.name}</p>
          </div>
          <button type="button" className="teacher-ranking-close" aria-label="Fechar ranking" onClick={onClose}>
            <X size={20} aria-hidden="true" />
          </button>
        </header>

        <div className="teacher-ranking-summary">
          <div>
            <span>Participantes</span>
            <strong>{mock?.answered || normalizedRanking.length}</strong>
          </div>
          <div>
            <span>Média geral</span>
            <strong>{averageScore}%</strong>
          </div>
          <div>
            <span>Questões</span>
            <strong>{totalQuestions || "-"}</strong>
          </div>
        </div>

        <div className="teacher-ranking-filters">
          <label className="teacher-ranking-search">
            <Search size={18} aria-hidden="true" />
            <input
              type="search"
              value={search}
              placeholder="Buscar aluno..."
              onChange={(event) => setSearch(event.target.value)}
            />
          </label>
          <select value={scoreFilter} onChange={(event) => setScoreFilter(event.target.value)}>
            <option value="all">Todas as notas</option>
            <option value="high">70% ou mais</option>
            <option value="medium">50% a 69%</option>
            <option value="low">Abaixo de 50%</option>
          </select>
        </div>

        <div className="teacher-ranking-content">
          {loading && <div className="teacher-ranking-state">Carregando ranking...</div>}
          {!loading && error && <div className="teacher-ranking-state teacher-ranking-error">{error}</div>}
          {!loading && !error && normalizedRanking.length === 0 && (
            <div className="teacher-ranking-state">Nenhum ranking disponível.</div>
          )}
          {!loading && !error && normalizedRanking.length > 0 && (
            <>
              <div className="teacher-ranking-podium">
                {podium.map((item) => (
                  <div key={item.id || item.userId || item.position} className={`teacher-ranking-podium-item podium-${item.position}`}>
                    <div className="teacher-ranking-medal">
                      <Trophy size={18} aria-hidden="true" />
                      <span>{item.position}</span>
                    </div>
                    <strong title={item.name}>{item.name}</strong>
                    <span>{item.correctAnswers}/{totalQuestions || "-"}</span>
                    <div>
                      <span>{item.position}º</span>
                    </div>
                  </div>
                ))}
              </div>

              <div className="teacher-ranking-table-wrap">
                <table className="teacher-ranking-table">
                  <thead>
                    <tr>
                      <th>POS.</th>
                      <th>ALUNO</th>
                      <th>ACERTOS</th>
                      <th>TEMPO</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredRanking.map((item) => (
                      <tr key={item.id || item.userId || item.position}>
                        <td>
                          <RankingPositionBadge position={item.position} />
                        </td>
                        <td>
                          <p className="teacher-ranking-student-name">{item.name}</p>
                          {item.email && (
                            <p className="teacher-ranking-student-email">
                              <Mail size={9} aria-hidden="true" />
                              {item.email}
                            </p>
                          )}
                        </td>
                        <td>
                          <span className="teacher-ranking-score-main">{item.correctAnswers}</span>
                          <span className="teacher-ranking-score-total">/{totalQuestions || "-"}</span>
                          <span className="teacher-ranking-score-percent">({item.score}%)</span>
                        </td>
                        <td>{item.time}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </aside>
    </div>
  );
}
